/**
 * @typedef {Object} BannerElementsFromDOM
 *
 * @property {HTMLElement} bannerElement - The main banner element.
 * @property {HTMLElement} innerBannerElement - The inner banner element.
 * @property {HTMLElement} bannerTextElement - The element displaying the banner text.
 * @property {HTMLElement} remainingTimeElement - The element displaying the remaining time.
 * @property {HTMLElement} warningText1Element - The first warning text element.
 * @property {HTMLElement} warningText2Element - The second warning text element.
 * @property {HTMLElement} warningText3Element - The third warning text element.
 * @property {HTMLElement} closedTextElement - The element displaying the closed text.
 */

/**
 * @typedef {Object} CloseAndWarningTimes
 *
 * @property {Date} close - The close time.
 * @property {number} warningSeconds - The number of seconds to display the first warning.
 * @property {number} finalWarningSeconds - The number of seconds to display the final warning.
 */

/**
 * @typedef {BannerElementsFromDOM & CloseAndWarningTimes} Options
 */

/**
 * The interval to check the remaining time in milliseconds
 * @private
 */
const CHECK_INTERVAL_MS = 1000;

/**
 * Flags used to avoid repeating work on the DOM
 * @private
 */
let _flags = {
  hasClassHidden: false,
  hasClassFinal: false,
};

/**
 * Sets a flag to a value.
 *
 * @param {string} flag - The flag to set.
 * @param {boolean} value - The value to set the flag to.
 * @returns {void}
 */
export function setFlag(flag, value) {
  _flags = {
    ..._flags,
    [flag]: value,
  };
}

/**
 * Gets a flag value.
 *
 * @param {string} flag
 * @returns {boolean}
 */
export function getFlag(flag) {
  return _flags[flag];
}

/**
 * Retrieves elements from the DOM.
 *
 * @returns {BannerElementsFromDOM | null} An object containing the elements or null if the root element is not found.
 */
export function getElementsFromDom() {
  const bannerElement = document.getElementById('out_of_hours_banner');

  // Return early if no banner element is found.
  if (bannerElement == null) {
    return null;
  }

  const innerBannerElement = document.getElementById(
    'out_of_hours_banner_inner'
  );
  const bannerTextElement = document.getElementById('out_of_hours_banner_text');
  const remainingTimeElement = document.getElementById(
    'out_of_hours_banner_time_remaining_time'
  );
  const warningText1Element = document.getElementById(
    'out_of_hours_banner_time_remaining_warning'
  );
  const warningText2Element = document.getElementById(
    'out_of_hours_banner_time_remaining_warning2'
  );
  const warningText3Element = document.getElementById(
    'out_of_hours_banner_time_remaining_warning3'
  );
  const closedTextElement = document.getElementById(
    'out_of_hours_banner_closed_text'
  );

  return {
    bannerElement,
    innerBannerElement,
    bannerTextElement,
    remainingTimeElement,
    warningText1Element,
    warningText2Element,
    warningText3Element,
    closedTextElement,
  };
}

/**
 * Retrieves close and warning times from the remaining time element.
 *
 * @param {HTMLElement | null} remainingTimeElement - The element containing the close and warning times.
 * @returns {CloseAndWarningTimes} An object containing the close time, warning seconds, and final warning seconds.
 */
export function getCloseAndWarningTimes(remainingTimeElement) {
  const dateString = remainingTimeElement?.dataset?.closeTime || '';
  const closeDate = dateString ? new Date(dateString) : new Date();

  const warningSecondsValue = dateString
    ? parseInt(remainingTimeElement.dataset.warningSeconds, 10)
    : 0;

  const finalWarningSecondsValue = dateString
    ? parseInt(remainingTimeElement.dataset.finalWarningSeconds, 10)
    : 0;

  // Ensure the values are valid and if not, set them to defaults.
  const close = isNaN(closeDate) ? new Date() : closeDate;
  const warningSeconds = isNaN(warningSecondsValue) ? 0 : warningSecondsValue;
  const finalWarningSeconds = isNaN(finalWarningSecondsValue) ? 0 : finalWarningSecondsValue;

  return {
    close,
    warningSeconds,
    finalWarningSeconds,
  };
}

/**
 * Calculates the seconds remaining until the close time.
 *
 * @param {Date} close - The close time.
 * @returns {number} The number of seconds remaining.
 */
export function getSecondsRemaining(close) {
  const now = new Date();
  return Math.floor((close - now) / 1000);
}

/**
 * Formats the remaining time as MM:SS.
 *
 * @param {number} secondsRemaining - The number of seconds remaining.
 * @returns {string} The time remaining formatted as `MM:SS`.
 */
export function getTimeRemainingText(secondsRemaining) {
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = Math.floor(secondsRemaining % 60);
  const minutesRemainingText = minutes < 10 ? '0' + minutes : minutes;
  const secondsRemainingText = seconds < 10 ? '0' + seconds : seconds;

  return minutesRemainingText + ':' + secondsRemainingText;
}

/**
 * Updates the text content of the remaining time element.
 *
 * @param {HTMLElement} remainingTimeElement - The element displaying the remaining time.
 * @param {number} secondsRemaining - The number of seconds remaining.
 * @returns {void}
 */
export function updateSecondRemainingText(
  remainingTimeElement,
  secondsRemaining
) {
  if (secondsRemaining >= 0) {
    remainingTimeElement.textContent = getTimeRemainingText(secondsRemaining);
  }
}

/**
 * Updates the display of the warning banner.
 *
 * @param {boolean} isWarning - Whether the warning should be displayed.
 * @param {Options} options - The banner element.
 * @returns {void}
 */
export function updateWarningDisplay(isWarning, options) {
  const { bannerElement } = options;

  if (isWarning && getFlag('hasClassHidden')) {
    bannerElement.classList.remove('out_of_hours_banner__hidden');
    bannerElement.removeAttribute('aria-hidden');
    setFlag('hasClassHidden', false);
  }
}

/**
 * Updates the display of the final warning banner.
 *
 * @param {boolean} isFinalWarning - Whether the final warning should be displayed.
 * @param {Options} options - The inner banner element.
 * @returns {void}
 */
export function updateFinalWarningDisplay(isFinalWarning, options) {
  const { innerBannerElement } = options;

  if (isFinalWarning && !getFlag('hasClassFinal')) {
    innerBannerElement.classList.add(
      'out_of_hours_banner__closing_warning--final'
    );
    setFlag('hasClassFinal', true);
  }
}

/**
 * Sets an element to hidden.
 *
 * @param {HTMLElement} element - The element to hide.
 * @returns {void}
 */
export function setElementToHidden(element) {
  element.classList.add('out_of_hours_banner__hidden');
  element.setAttribute('aria-hidden', 'true');
}

/**
 * Sets an element to visible.
 *
 * @param {HTMLElement} element - The element to show.
 * @returns {void}
 */
export function setElementToVisible(element) {
  element.classList.remove('out_of_hours_banner__hidden');
  element.removeAttribute('aria-hidden');
}

/**
 * Updates the display when the service is closed.
 *
 * @param {boolean} isServiceClosed - Whether the service is closed.
 * @param {Options} options - The elements to update.
 * @returns {void}
 */
export function updateServiceClosedDisplay(isServiceClosed, options) {
  const {
    warningText1Element,
    warningText2Element,
    warningText3Element,
    remainingTimeElement,
    closedTextElement,
    bannerTextElement,
  } = options;

  if (isServiceClosed) {
    setElementToHidden(warningText1Element);
    setElementToHidden(warningText2Element);
    setElementToHidden(warningText3Element);
    setElementToHidden(remainingTimeElement);
    setElementToVisible(closedTextElement);
    bannerTextElement.setAttribute('aria-live', 'polite');
  }
}

/** @type {typeof setInterval | null} */
let interval = null;

/**
 * Wraps the interval to clean up when checkElements returns false
 * 
 * @param {Options} options
 * @returns {void}
 */
export function intervalCallback(options) {
  const shouldContinue = checkElements(options);
  if (!shouldContinue) {
    clearInterval(interval);
  }
}

/**
 * Checks the elements and updates their display based on the remaining time.
 *
 * @param {Options} options - The elements and times to check.
 * @returns {boolean} True if the interval should continue, otherwise false.
 */
export function checkElements(options) {
  if (!options) return false;

  const { remainingTimeElement, close, warningSeconds, finalWarningSeconds } =
    options;

  const secondsRemaining = getSecondsRemaining(close);
  const isWarning = secondsRemaining <= warningSeconds;
  const isFinalWarning = secondsRemaining <= finalWarningSeconds;
  const isServiceClosed = secondsRemaining <= 0;

  updateSecondRemainingText(remainingTimeElement, secondsRemaining);
  updateWarningDisplay(isWarning, options);
  updateFinalWarningDisplay(isFinalWarning, options);
  updateServiceClosedDisplay(isServiceClosed, options);

  return !isServiceClosed;
}

/**
 * Sets up the interval to check and update the elements.
 *
 * @param {BannerElementsFromDOM} elements - The elements to check and update.
 * @returns {void}
 */
export function setupInterval(elements) {
  if (!elements) return;

  const { bannerElement, innerBannerElement, remainingTimeElement } = elements;

  setFlag(
    'hasClassHidden',
    bannerElement.classList.contains('out_of_hours_banner__hidden')
  );
  setFlag(
    'hasClassFinal',
    innerBannerElement.classList.contains(
      'out_of_hours_banner__closing_warning--final'
    )
  );

  /** @type {Options} */
  const options = {
    ...elements,
    ...getCloseAndWarningTimes(remainingTimeElement),
  };

  interval = setInterval(() => intervalCallback(options), CHECK_INTERVAL_MS);
}

/**
 * Initializes the script by retrieving elements and setting up the interval.
 * It will bail early if no banner element is found.
 *
 * @returns {void}
 */
export function initialise() {
  const elements = getElementsFromDom();
  if (elements == null) return;

  setupInterval(elements);
}

export default {
  initialise,
};
